/**
 * Client-side PDF Generator Registry
 * Maps exam type keys to @react-pdf/renderer components.
 * Mirrors server/pdfGeneratorFactory.js — no PHI ever leaves the browser.
 */

import React from 'react';

import { RenalPDF }                       from './general/RenalPDF';
import { AbdomenPDF }                     from './general/AbdomenPDF';
import { ScrotalPDF }                     from './general/ScrotalPDF';
import { ThyroidPDF }                     from './general/ThyroidPDF';
import { PelvicPDF }                      from './general/PelvicPDF';
import { FollicleCountPDF }               from './general/FollicleCountPDF';
import { AortaPDF }                       from './vascular/AortaPDF';
import { CarotidPDF }                     from './vascular/CarotidPDF';
import { SoftTissuePDF }                  from './vascular/SoftTissuePDF';
import { RenalArteryDopplerPDF }          from './vascular/RenalArteryDopplerPDF';
import { LegArterialPDF }                 from './vascular/LegArterialPDF';
import { UpperExtremityArterialPDF }      from './vascular/UpperExtremityArterialPDF';
import { DialysisAccessPDF }              from './vascular/DialysisAccessPDF';
import { LegDVTPDF }                      from './dvt/LegDVTPDF';
import { UpperExtremityDVTPDF }           from './dvt/UpperExtremityDVTPDF';
import { FirstTrimesterOBPDF }            from './obstetric/FirstTrimesterOBPDF';
import { SecondTrimesterMultipleOBPDF }   from './obstetric/SecondTrimesterMultipleOBPDF';
import { BPPPDF }                         from './obstetric/BPPPDF';
import { FollowUpOBPDF }                  from './obstetric/FollowUpOBPDF';
import { PediatricAbdomenPDF }            from './pediatric/PediatricAbdomenPDF';
import { PediatricBrainSpinePDF }         from './pediatric/PediatricBrainSpinePDF';
import { PediatricHipsPDF }              from './pediatric/PediatricHipsPDF';
import { LungScreeningPDF }               from './screening/LungScreeningPDF';

/**
 * Registry: examType key → React component
 * Keys must exactly match those used in the server EXAM_REGISTRY and form examType values.
 */
export const CLIENT_PDF_REGISTRY = {
  'Renal':                      RenalPDF,
  'Abdomen':                    AbdomenPDF,
  'Scrotal':                    ScrotalPDF,
  'Thyroid':                    ThyroidPDF,
  'Pelvic':                     PelvicPDF,
  'Follicle Count':             FollicleCountPDF,
  'Aorta':                      AortaPDF,
  'Carotid':                    CarotidPDF,
  'Soft Tissue':                SoftTissuePDF,
  'Renal Artery Doppler':       RenalArteryDopplerPDF,
  'Lower Extremity Arterial':   LegArterialPDF,
  'Upper Extremity Arterial':   UpperExtremityArterialPDF,
  'Dialysis Access':            DialysisAccessPDF,
  'DVT':                        LegDVTPDF,
  'UpperExtremityDVT':          UpperExtremityDVTPDF,
  'First Trimester OB':         FirstTrimesterOBPDF,
  'Second Trimester OB':        SecondTrimesterMultipleOBPDF,
  'BPP':                        BPPPDF,
  'Follow Up OB':               FollowUpOBPDF,
  'Pediatric Abdomen':          PediatricAbdomenPDF,
  'Pediatric Brain and Spine':  PediatricBrainSpinePDF,
  'Pediatric Hips':             PediatricHipsPDF,
  'Lung Cancer Screening':      LungScreeningPDF,
};

/**
 * Generate a PDF Blob entirely in the browser.
 * No PHI is transmitted to the server.
 *
 * @param {string} examType       - Key from CLIENT_PDF_REGISTRY
 * @param {Object} formData       - Full form state object
 * @param {Object} facilityInfo   - Facility details pre-fetched at login (no PHI)
 * @returns {Promise<Blob>}
 */
export async function generatePDFInBrowser(examType, formData, facilityInfo) {
  const { pdf } = await import('@react-pdf/renderer');

  const PdfComponent = CLIENT_PDF_REGISTRY[examType];
  if (!PdfComponent) {
    throw new Error(`No client PDF generator registered for exam type: "${examType}"`);
  }

  const blob = await pdf(
    <PdfComponent data={formData} facilityInfo={facilityInfo || {}} />
  ).toBlob();

  return blob;
}

/**
 * Trigger a browser file download for a Blob.
 */
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * Open a Blob in a new browser tab (PDF preview).
 * Returns the window reference or throws if popup is blocked.
 */
export function previewBlob(blob) {
  const url = URL.createObjectURL(blob);
  const previewWindow = window.open(url, '_blank');
  if (!previewWindow) {
    URL.revokeObjectURL(url);
    throw new Error('Preview blocked by browser. Please allow pop-ups for this site and try again.');
  }
  previewWindow.addEventListener('load', () => URL.revokeObjectURL(url));
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return previewWindow;
}
